import { getFirstPodcast } from "../private/api/getFirstPodcast";
import { getPreviewPodcast } from "../private/api/getPreviewPodcast";
import { getSignedUrl } from "../private/api/getSignedUrl";
import { getOtherPodcastList } from "../private/api/otherPodcasts";

const Audioplayer = {
  namespaced: true,

  state: {
    list: {},
    otherPodcasts: {},

    bufferedRanges: [],
    audioplayer: null,

    isPlaying: false,
    isLoading: false,
    volume: 1,
    speed: 1,
    currentTime: 0,

    activePodcastId: null,
    podcastPreviewText: null,

    isAudioplayerLoading: false,
    isAudioplayerReady: false,
    isStickyPanelOpened: false,
    isLoadingPodcastsList: false,

    currentAudioRequest: null,
    wasTimeRestoredFromStorage: false,
  },

  actions: {
    async initPlayer({ commit, dispatch, state }) {
      await dispatch('initFirstPodcast');
      commit('SET_AUDIOPLAYER_API');

      // Инициализация MediaSession (кнопки локскрина/гарнитуры/шторки).
      // В iOS Home Screen PWA обработчики часто срабатывают в более "жёстких" условиях (фон/заморозка),
      // поэтому стараемся делать play/pause максимально простыми и быстрыми.
      if ('mediaSession' in navigator) {
        // Важно для iOS: стараться вызывать audioplayer.play() напрямую из handler'а,
        // чтобы не "терять" пользовательское действие в длинной async-цепочке.
        navigator.mediaSession.setActionHandler('play', async () => {
          try {
            if (!state.audioplayer) throw new Error('Аудиоплеер не инициализирован');
            // На iOS PWA иногда после паузы/возобновления "теряется" звук при продолжающемся таймере.
            // Принудительно возвращаем ожидаемые настройки перед play().
            state.audioplayer.muted = false;
            commit('SET_VOLUME', state.volume);
            commit('SET_SPEED', state.speed);
            state.audioplayer.currentTime = state.currentTime;
            await state.audioplayer.play();
            commit('SET_PLAYING_FLAG', true);
          } catch (error) {
            console.error('MediaSession play failed, fallback to resumeAudio:', error);
            // Если прямой запуск не удался (например, нужен сетевой запрос/переинициализация) — fallback на общий сценарий.
            try {
              await dispatch('resumeAudio');
            } catch (resumeError) {
              // Подсказка пользователю показана внутри resumeAudio; здесь просто не даём промису "всплыть" наружу.
              console.error('MediaSession resumeAudio failed:', resumeError);
            }
          }
        });

        navigator.mediaSession.setActionHandler('pause', () => {
          if (!state.audioplayer) return;
          state.audioplayer.pause();
          commit('SET_PLAYING_FLAG', false);
        });

        navigator.mediaSession.setActionHandler('stop', () => {
          if (!state.audioplayer) return;
          state.audioplayer.pause();
          commit('SET_PLAYING_FLAG', false);
        });
        navigator.mediaSession.setActionHandler('seekbackward', (details) => {
          const skip = details.seekOffset || 10;
          dispatch('fowardToPlace', { time: Math.max(0, state.currentTime - skip), idPodcast: state.activePodcastId });
        });
        navigator.mediaSession.setActionHandler('seekforward', (details) => {
          const skip = details.seekOffset || 10;
          dispatch('fowardToPlace', { time: state.currentTime + skip, idPodcast: state.activePodcastId });
        });
        navigator.mediaSession.setActionHandler('seekto', (details) => {
          dispatch('fowardToPlace', { time: details.seekTime, idPodcast: state.activePodcastId });
        });
      }

      const savedVolume = localStorage.getItem('audio_volume');
      commit('SET_VOLUME', savedVolume !== null ? parseFloat(savedVolume) : 0.5);

      const savedSpeed = localStorage.getItem('audio_speed');
      commit('SET_SPEED', savedSpeed !== null ? parseFloat(savedSpeed) : 1);

      commit('SET_READY_AUDIOPLAYER_FLAG');

      // Если ранее была неудачная попытка запуска из локскрина (например, iOS Home Screen PWA в фоне),
      // показываем подсказку при следующем открытии приложения.
      if (localStorage.getItem('audio_requires_foreground_hint')) {
        localStorage.removeItem('audio_requires_foreground_hint');
        await dispatch('showPlaybackRequiresForegroundHint');
      }
    },

    async initFirstPodcast({ commit }) {
      commit('SET_LOADING_AUDIOPLAYER_FLAG', true);
      const data = await getFirstPodcast();
      const podcast = data[0].podcast_atributes;
      commit('SET_NEW_AUDIO', podcast);
      commit('WRITE_ID_ACTIVE_PODCAST', podcast.id);
      commit('SET_LOADING_AUDIOPLAYER_FLAG', false);
    },

    async initPreviewPodcast({ commit }) {
      commit('SET_LOADING_AUDIOPLAYER_FLAG', true);
      const data = await getPreviewPodcast();
      commit('SET_PREVIEW', data.title);
      commit('SET_LOADING_AUDIOPLAYER_FLAG', false);
    },

    async activeCurrentPodcats({ commit, dispatch, state }) {
      commit('TOGGLE_STICKY_PANEL', true);
      commit('SET_LOADING_AUDIO_FLAG', true);
      try {
        let currentPodcast = state.list[state.activePodcastId];

        if (!currentPodcast?.signed_url || Audioplayer.actions._shouldRefreshLink(currentPodcast)) {
          await dispatch('useSignedUrlForAudio');
          currentPodcast = state.list[state.activePodcastId]; // обновляем данные после получения signed_url
        }

        if (state.audioplayer.src !== currentPodcast.signed_url) {
          commit('SET_AUDIO_URL', currentPodcast.signed_url);

          await new Promise((resolve, reject) => {
            const timer = setTimeout(() => reject(new Error('Timeout loading audio')), 10000);
            const onCanPlay = () => {
              clearTimeout(timer);
              state.audioplayer.removeEventListener('canplay', onCanPlay);
              resolve();
            };
            state.audioplayer.addEventListener('canplay', onCanPlay);
            if (state.audioplayer.readyState > 0) onCanPlay();
          });

          if (
            !state.wasTimeRestoredFromStorage &&
            localStorage.getItem('last_audio_id') === String(state.activePodcastId)
          ) {
            const savedTime = parseFloat(localStorage.getItem('last_remember_time') || '0');
            if (!isNaN(savedTime)) {
              commit('SET_CURRENT_TIME', savedTime);
              state.audioplayer.currentTime = savedTime;
            }
            state.wasTimeRestoredFromStorage = true;
          } else {
            state.audioplayer.currentTime = state.currentTime;
          }
        }

        // Обновляем MediaSession metadata
        if ('mediaSession' in navigator) {
          navigator.mediaSession.metadata = new MediaMetadata({
            title: currentPodcast.title || '',
            artist: currentPodcast.author || 'Drops Capital',
            artwork: currentPodcast.image ? [{ src: currentPodcast.image, sizes: '512x512', type: 'image/png' }] : []
          });
        }

        commit('SET_VOLUME', state.volume);
        commit('SET_SPEED', state.speed);
        commit('SET_CURRENT_TIME', state.currentTime);

        // На iOS PWA после паузы/возобновления иногда остаётся "тихое" воспроизведение.
        // Подстраховываемся: перед play() принудительно снимаем mute.
        state.audioplayer.muted = false;
        state.audioplayer.currentTime = state.currentTime;
        await state.audioplayer.play();
        commit('SET_PLAYING_FLAG', true);
      } catch (error) {
        console.error('Playback failed:', error);
        commit('SET_PLAYING_FLAG', false);
        throw error;
      } finally {
        commit('SET_LOADING_AUDIO_FLAG', false);
      }
    },

    _shouldRefreshLink(podcast) {
      if (!podcast.signed_url || !podcast.expires_at) return true;
      const expiresAt = new Date(podcast.expires_at);
      if (isNaN(expiresAt.getTime())) return true;

      // Обновляем ссылку незадолго до истечения (фиксированное окно),
      // чтобы не делать лишние запросы при коротком TTL (например, 5 минут).
      const remainingMs = expiresAt.getTime() - Date.now();
      return remainingMs <= 60_000; // 60 секунд до окончания
    },

    async useSignedUrlForAudio({ state, commit }) {
      if (state.currentAudioRequest) {
        state.currentAudioRequest.abort?.();
      }

      const currentPodcast = state.list[state.activePodcastId];
      if (!currentPodcast || !currentPodcast.audio_file) return;

      if (!Audioplayer.actions._shouldRefreshLink(currentPodcast)) return;

      const match = currentPodcast.audio_file.match(/\/([^/?#]+)$/);
      if (!match) return;
      const name = match[1];
      const request = getSignedUrl(name);
      commit('SET_AUDIO_REQUEST', request);

      try {
        const data = await request;
        const cleanSignedUrl = data.signed_url.replace(/\.sdn/, '');
        commit('UPDATE_PODCAST_SIGNED_URL', {
          id: state.activePodcastId,
          signed_url: cleanSignedUrl,
          /* signed_url: 'https://www.soundhelix.com/examples/mp3/SoundHelix-Song-1.mp3', */
          expires_at: data.expires_at
        });
      } catch (error) {
        console.error('Failed to get signed URL:', error);
        throw error;
      }
    },

    async changeVolume({ commit }, volume) {
      commit('SET_VOLUME', volume);
      localStorage.setItem('audio_volume', volume);
    },

    async changeSpeed({ commit }, speed) {
      commit('SET_SPEED', speed);
      localStorage.setItem('audio_speed', speed);
    },

    async closeStickyPanel({ state, commit }, status) {
      commit('TOGGLE_STICKY_PANEL', status);
      await state.audioplayer.pause();
      commit('SET_PLAYING_FLAG', false);
    },

    async playAudio({ state, commit, dispatch }, { id }) {
      if (state.isPlaying) {
        await dispatch('pauseAudio');
      }
      commit('SET_CURRENT_TIME', 0);
      commit('WRITE_ID_ACTIVE_PODCAST', id);
      state.wasTimeRestoredFromStorage = false;
      dispatch('activeCurrentPodcats');
    },

    async pauseAudio({ state, commit }) {
      if (!state.audioplayer || !state.isPlaying) return false;
      await state.audioplayer.pause();
      commit('SET_PLAYING_FLAG', false);
    },

    async resumeAudio({ state, commit, dispatch }, seekTime) {
      if (!state.audioplayer) throw new Error('Аудиоплеер не инициализирован');
      const validatedTime = typeof seekTime === 'number' && !isNaN(seekTime)
        ? Math.max(0, seekTime)
        : state.currentTime;
      commit('SET_CURRENT_TIME', validatedTime);

      // Быстрая попытка возобновить без лишней загрузки/await:
      // - помогает iOS (особенно Home Screen PWA), где play() важно вызвать максимально близко к действию пользователя;
      // - не подходит, если signed_url протух и требуется запрос за новой ссылкой.
      const currentPodcast = state.activePodcastId ? state.list[state.activePodcastId] : null;
      const canQuickResume = Boolean(
        currentPodcast?.signed_url
        && !Audioplayer.actions._shouldRefreshLink(currentPodcast)
        && state.audioplayer.src === currentPodcast.signed_url
      );

      if (canQuickResume) {
        try {
          commit('SET_VOLUME', state.volume);
          commit('SET_SPEED', state.speed);
          state.audioplayer.muted = false;
          state.audioplayer.currentTime = validatedTime;
          await state.audioplayer.play();
          commit('SET_PLAYING_FLAG', true);
          return true;
        } catch (error) {
          console.error('Quick resume failed, fallback to full start:', error);
        }
      }

      try {
        return await dispatch('activeCurrentPodcats');
      } catch (error) {
        // Для iOS PWA частый кейс: воспроизведение не стартует из локскрина/фона.
        // В этом случае просим пользователя открыть приложение.
        localStorage.setItem('audio_requires_foreground_hint', '1');
        await dispatch('showPlaybackRequiresForegroundHint');
        throw error;
      }
    },

    async fowardToPlace({ state, commit }, { time }) {
      if (!state.audioplayer) return;

      // Перемотка должна быть безопасной: время ограничиваем диапазоном [0..duration] (если duration известна).
      const duration = Number.isFinite(state.audioplayer.duration) ? state.audioplayer.duration : null;
      const safeTimeRaw = typeof time === 'number' && !isNaN(time) ? Math.max(0, time) : 0;
      const safeTime = duration !== null ? Math.min(safeTimeRaw, duration) : safeTimeRaw;

      commit('SET_CURRENT_TIME', safeTime);
      state.audioplayer.currentTime = safeTime;
    },

    async showPlaybackRequiresForegroundHint({ dispatch }) {
      // Явный фолбек для пользователя: iOS Home Screen PWA не гарантирует старт/возобновление аудио с экрана блокировки.
      const ua = navigator.userAgent || '';
      const isIOS = /iPad|iPhone|iPod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
      const text = isIOS
        ? 'Не удалось запустить подкаст. Если вы на iPhone и нажимаете Play на экране блокировки — откройте приложение и нажмите Play внутри.'
        : 'Не удалось запустить подкаст.';
      await dispatch('OverlayManager/addMessage', {
        text,
        color: 'red',
        icon: 'lock',
      }, { root: true });
    },

    async addPodcastToList({ commit }, podcast) {
      commit('SET_NEW_AUDIO', podcast);
    },

    async toGetNewPodcastsData({ commit }, exclude) {
      try {
        commit('SET_LOADING_PODCASTS_LIST_STATUS', true);
        commit('CLEAR_OTHER_LIST');
        const data = await getOtherPodcastList(exclude);
        data.results.forEach(item => {
          commit('SET_OTHER_LIST', {
            id: item.id,
            object_id: item.object_id,
            audio_length: item.audio_length,
            comments_count: item.comments_count,
            title: item.title,
            date: item.date,
          });
        });
      } catch (error) {
        console.error(`Ошибка при получении подкастов: ${error}`);
      } finally {
        commit('SET_LOADING_PODCASTS_LIST_STATUS', false);
      }
    },
  },

  mutations: {
    SET_AUDIOPLAYER_API(state) {
      state.audioplayer = new Audio();
      // Держим preload включённым, чтобы "resume" чаще проходил без ожидания загрузки.
      state.audioplayer.preload = 'auto';

      state.audioplayer.addEventListener('timeupdate', () => {
        if (!state.isPlaying) return;

        state.currentTime = state.audioplayer.currentTime;

        if (state.activePodcastId) {
          localStorage.setItem('last_audio_id', state.activePodcastId);
          localStorage.setItem('last_remember_time', state.currentTime);
        }

        if ('mediaSession' in navigator) {
          navigator.mediaSession.setPositionState?.({
            duration: state.audioplayer.duration || 0,
            playbackRate: state.speed,
            position: state.currentTime
          });
        }
      });

      state.audioplayer.addEventListener('loadstart', () => {
        state.isLoading = true;
      });

      state.audioplayer.addEventListener('canplay', () => {
        state.isLoading = false;
      });

      state.audioplayer.addEventListener('progress', () => {
        const buffered = state.audioplayer.buffered;
        const ranges = [];

        for (let i = 0; i < buffered.length; i++) {
          ranges.push({
            start: buffered.start(i),
            end: buffered.end(i),
          });
        }

        state.bufferedRanges = ranges;
      });
    },

    UPDATE_PODCAST_SIGNED_URL(state, { id, signed_url, expires_at }) {
      state.list[id].signed_url = signed_url;
      state.list[id].expires_at = expires_at;
    },

    SET_VOLUME(state, volume) {
      const validatedVolume = Number(volume);
      state.volume = isNaN(validatedVolume) || validatedVolume < 0 || validatedVolume > 1 ? 0.5 : validatedVolume;
      if (state.audioplayer) state.audioplayer.volume = state.volume;
    },

    SET_SPEED(state, speed) {
      const validatedSpeed = Number(speed);
      state.speed = isNaN(validatedSpeed) || validatedSpeed <= 0 || validatedSpeed > 4 ? 1 : validatedSpeed;
      if (state.audioplayer) state.audioplayer.playbackRate = state.speed;
    },

    SET_AUDIO_URL(state, url) {
      state.audioplayer.src = url;
    },

    TOGGLE_STICKY_PANEL(state, status) {
      state.isStickyPanelOpened = status;
    },

    WRITE_ID_ACTIVE_PODCAST(state, id) {
      state.activePodcastId = id;
    },

    SET_NEW_AUDIO(state, podcast) {
      state.list[podcast.id] = podcast;
    },

    SET_READY_AUDIOPLAYER_FLAG(state) {
      state.isAudioplayerReady = true;
    },

    SET_PREVIEW(state, text) {
      state.podcastPreviewText = text;
    },

    SET_LOADING_AUDIOPLAYER_FLAG(state, status) {
      state.isAudioplayerLoading = status;
    },

    SET_LOADING_AUDIO_FLAG(state, status) {
      state.isLoading = status;
    },

    SET_PLAYING_FLAG(state, status) {
      state.isPlaying = status;
      // Обновляем MediaSession.playbackState, чтобы системные контролы (локскрин/шторка) отображали корректное состояние.
      if ('mediaSession' in navigator && 'playbackState' in navigator.mediaSession) {
        navigator.mediaSession.playbackState = status ? 'playing' : 'paused';
      }
    },

    SET_AUDIO_REQUEST(state, request) {
      state.currentAudioRequest = request;
    },

    SET_CURRENT_TIME(state, time) {
      state.currentTime = time;
    },

    SET_OTHER_LIST(state, data) {
      state.otherPodcasts[data.id] = data;
    },

    SET_LOADING_PODCASTS_LIST_STATUS(state, status) {
      state.isLoadingPodcastsList = status;
    },

    CLEAR_OTHER_LIST(state){
      state.otherPodcasts = {};
    },
  },

  getters: {},
};

export default Audioplayer;
